/**
 * Copyright (c) Microsoft. All rights reserved.
 * Internal type definitions for Trouter JavaScript Client
 */

import { calculateTtlInSec } from './Common';
import {
    HttpHeaders, IConnectionCache, IConnectionInfo, ITelemetrySender, SendResponseResult,
    TimeoutOptions, TrouterTelemetrySettings
} from './Interfaces';


export interface ITrouterRequest {
    startTS: number;
    url: string;
    shortUrl: string;
    body: string;
    headers: HttpHeaders;
    id: number;
    method: string;
    correlationVector: string;
}

export interface IMessageLossIndicator {
    /** flow tag representing the type of lost messages */
    tag: string;

    /**
     * etag of the particular flow tag, the etag is used to identify the
     * flow tag on the server when sending trouter.processed_message_loss event
     * to be able to determine if new message loss happened after this one or not
     */
    etag: string;
}

/**
 * Response to downstream service
 */
export interface ITrouterResponse {
    writeHead(status: number, headers?: HttpHeaders): void;
    write(chunk: string): void;
    end(chunk?: string): SendResponseResult;
}

/**
 * Data to generate TC query param and for telemetry
 */
export interface IClientInfo {
    /** User-Agent string, for telemetry tracking */
    ua: string;
    /** Application client version, for telemetry tracking */
    v: string;
    /** Trouter client version */
    cv?: string;
}

/**
 * Socket.IO (aka "transport") options
 */
export interface ISocketIoOptions {
    'force new connection'?: boolean;
    'reconnect'?: boolean;
    'query'?: string;
    /** timeout for each transport */
    'connect timeout'?: number;
    transports?: string[];
    /**
     * Timeout for waiting for ACK from server for trouter response (i.e. POST HTTP request) in XHR mode
     */
    ackTimeoutMs: number;

    /**
     * Additional request headers that will be added both to the socket.io handshake request,
     * and to every longpoll GET/POST request
     */
    requestHeaders?: Record<string, string>;
    /**
     * When using the /v4/c protocol, the socket.io handshake request will not be sent, instead
     * the values that would normally be part of the response to it will be provided
     * by the client in this option. Only the WebSocket transport will be available, fallback to longpoll
     * will be performed in a different way (restarting the connection process from the beginning)
     * @property websocketUrl needs to be a wss:// URL, not https://
     */
    'skipped handshake data'?: { timeout: number, websocketUrl: string };
    rewriteUrlForProxy(url: string): string;
}

/**
 * EDF registrar options
 */
export interface IRegistrarOptions {
    registrarUrl: string;
    registrationId: string;

    pnhAppId: string;
    platform: string;
    pnhTemplateKey: string;
    platformUIVersion: string;
    productContext?: string;
    context: string;

    /** If set - limits registration TTL in EDF. If not - value from Trouter is used */
    registrarTtlSec?: number;
}

/**
 * Options passed from outside
 */
export interface IConfiguration {
    clientInfo: IClientInfo;

    eventLogger: ITelemetrySender;
    telemetrySettings: TrouterTelemetrySettings;  // Client telemetry configuration settings

    clientCorrelationID: string;  // Client telemetry correlation ID, for correlation between Trouter and other components
    environment: string;  // Environment for telemetry, e.g. "latest", "liveint", "dogfood", "prod"

    ioOptions?: ISocketIoOptions;  // Socket.IO options
    io?: Record<string, unknown>;  // Socket.IO override; for tests only
}

/**
 * Options passed from outside
 */
export interface ITrouterOptions extends IConfiguration {
    endpointId?: string;
    trouterUrl: string;
    registration?: IRegistrarOptions;
    connectionDependsOnRegistration: () => boolean;
    delayEventsUntilRegistered: () => boolean;

    /**
     * timeouts for normal mode
     */
    timeoutOptions: TimeoutOptions;

    /**
     * timeouts for in-call mode, i.e. with more aggressive timeouts/reconnect policy
     */
    incallTimeoutOptions: TimeoutOptions;

    /**
     * Timeout for being in 'in-call' mode, i.e. with aggressive ping/retry policy. If it's zero - in-call mode is disabled
     */
    incallModeTimeoutMs: number;

    /**
     * Delay for keeping a previous connection open after connection switching.
     */
    lingeringConnectionDelayMs: number;

    /**
     * After how long to send the user activity second time after reconnect.
     */
    userActivitySecondResendDelayMs: number;

    /**
     * How soon after a connection is established must a disconnect packet with
     * the "dup" reason come to be recognized as a "duplicate loop/storm" with
     * another client sharing the same connection cache and trigger recovery.
     */
    duplicateDisconnectThresholdMs: number;

    connectionCache?: IConnectionCache;

    /**
     * Optional setting to put a limit on maximum number of token fetch retries
     */
    retryLimitOnTokenFetch?: number;

    /**
     * The goal is that normally, getting an AAD token will make the client use the /v4/c protocol.
     * This setting can be used by tests to make sure that even the /v4/a protocol works with AAD tokens.
     * It can also be used to delay the rollout of the /v4/c protocol until we want to use it everywhere.
     */
    forceV4aProtocol?: boolean;

    /** See TrouterSettings.extraConnectionHeaders */
    extraConnectionHeaders?: Record<string, string>;

    /**
     * When calling start() when Trouter is already running, any pending backoffs will be reset.
     * To avoid resetting it too often (e.g. when an event that triggers start() is called too often),
     * this behavior is additionally throttled to only once per time period specified here.
     */
    expediteBackoffOnStartMinimumDelayMs?: number;

    registrationStateCallback?(isRegistered: boolean): void;

    rewriteUrlForProxy(this: void, url: string): string;

    toJSON?(): unknown;
}

/**
 * Information about server connection
 */
export class ServerState implements IConnectionInfo {
    public constructor(
        /** Trouter client connection ID */
        public connectionId: string,
        /** Trouter client connected client ID */
        public connectedClientId: string,
        /** window.location.hostname */
        public domId: string,
        /** Unsecure Trouter url at which client can be reached */
        public unsecureUrl: string,
        /** Secure Trouter url at which client can be reached */
        public url: string,
        /** URL base to use for client-to-client requests */
        public c2cUrlBase: string,
        /** expiration timestamp for connection in seconds */
        public expirationTsSec?: number
    ) {
    }

    /** Return connection TTL, i.e. number of seconds till expiration */
    public getRemainingTtlInSec(): number {
        return calculateTtlInSec(this.expirationTsSec);
    }
}

/**
 * Interface for trouter URL promise resolver
 */
export interface ITrouterUrlPromise {
    resolveUrl(url: string): void;
    resetUrl(): void;
}

export enum UserActivityEventReason {
    Unknown = 0,
    Modified = 1,
    Snapshot = 2,
    Connected = 3
}

export type SignatureData = {
    sr: string,
    issuer: string,
    sp: string,
    se: string,
    st: string,
    scae?: string,
    sig: string
};

export type AnonymousWebsocketAuthentication = {
    headers: Record<string, string>
    connectparams?: Partial<SignatureData>
};

/**
 * Duplicating TrouterState since too many parts of SCC depend on it for us to change it internally.
 * Don't expose this to the outside world, to avoid another repeat of this issue.
 */
export enum TrouterManagerState {
    /** Initial state */
    Unknown = 0,

    /** ready to work */
    Connected = 2,

    /** waiting for 'connected' event */
    Disconnected = 3,

    /** waiting for 'register' from second connection */
    Switching = 9,

    /** Unable to continue, in final state. */
    TerminalError = 10,
}



// WEBPACK FOOTER //
// ./lib_temp/InternalInterfaces.ts